import { useMemo } from 'react';
import { useQuery } from 'data-fetching-client';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import FullAddressAdminArea from './FullAddressAdminArea';
import FullAddressField from './FullAddressField';
import CountryInput from './CountryInput';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import { GET_COUNTRY_METADATA } from '../../data/queries';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const VALID_FIELDS = {
  ADDRESS_LINE_1: 'ADDRESS_LINE_1',
  ADDRESS_LINE_2: 'ADDRESS_LINE_2',
  LOCALITY: 'LOCALITY',
  POSTAL_CODE: 'POSTAL_CODE',
  ADMIN_AREA: 'ADMIN_AREA'
};
const FIELD_MAP = {
  ADDRESS_LINE_1: 'address',
  ADDRESS_LINE_2: 'address2',
  LOCALITY: 'city',
  POSTAL_CODE: 'postalCode',
  ADMIN_AREA: 'state'
};
const GRID_SIZES = {
  SHORT: 'SHORT',
  LONG: 'LONG',
  FULL: 'FULL'
};
const GRID_SIZE_FROM_FIELD = {
  [GRID_SIZES.FULL]: 12,
  [GRID_SIZES.LONG]: 8,
  [GRID_SIZES.SHORT]: 4
};
export const useCountryMetadata = countryCode => {
  const {
    data,
    loading,
    previousData
  } = useQuery(GET_COUNTRY_METADATA, {
    variables: {
      countryCode
    },
    skip: !countryCode
  });
  const countryMetadata = (data === null || data === void 0 ? void 0 : data.countryMetadata) || (previousData === null || previousData === void 0 ? void 0 : previousData.countryMetadata);
  return {
    countryMetadata: countryMetadata,
    loading
  };
};
const FullAddress = ({
  fullAddress,
  selectableCountries,
  onChange,
  isShippingAddress = false
}) => {
  const countryCode = fullAddress.country;
  const isUS = Boolean(countryCode && countryCode.toUpperCase() === 'US');
  const {
    countryMetadata,
    loading
  } = useCountryMetadata(countryCode);
  const {
    formState
  } = usePaymentFormContext();
  const paymentIsLoading = formState.isCheckoutSubmitPending;
  const disabled = paymentIsLoading || loading;
  const states = useMemo(() => {
    if (!countryMetadata) {
      return [];
    }
    return countryMetadata.states.map(option => ({
      text: option.name,
      value: option.key,
      key: option.name
    }));
  }, [countryMetadata]);
  const fields = useMemo(() => {
    if (!countryMetadata) {
      return [];
    }
    return countryMetadata.fields.filter(field => field.type in VALID_FIELDS).map(field => ({
      key: FIELD_MAP[field.type],
      type: field.type,
      width: field.width
    }));
  }, [countryMetadata]);

  /*
  NOTE: throughout this app, we use horizontal spacing between our form fields
  that does not conform to our standard UIGrid spacing. The following code
  allows us to use UIGrid but maintain app-specific horizontal spacing
  specifically in the case that the state dropdown and postal code input fields
  need to be rendered side-by-side.
  */
  const needsIdiosyncraticStyling = useMemo(() => {
    const indexFieldAdminArea = fields.findIndex(field => field.type === VALID_FIELDS.ADMIN_AREA);
    const indexFieldPostalCode = fields.findIndex(field => field.type === VALID_FIELDS.POSTAL_CODE);
    return indexFieldAdminArea && indexFieldPostalCode && indexFieldPostalCode === indexFieldAdminArea + 1 && fields[indexFieldAdminArea].width === GRID_SIZES.LONG && fields[indexFieldPostalCode].width === GRID_SIZES.SHORT;
  }, [fields]);
  return /*#__PURE__*/_jsxs(Grid, {
    children: [/*#__PURE__*/_jsx(GridItem, {
      size: 12,
      children: /*#__PURE__*/_jsx(CountryInput, {
        validCountries: selectableCountries,
        value: countryCode,
        onChange: event => onChange('country', event),
        disabled: disabled,
        isShippingAddress: isShippingAddress
      })
    }), fields.map(({
      key,
      type,
      width
    }) => /*#__PURE__*/_jsx(GridItem, {
      paddingInlineStart: needsIdiosyncraticStyling && type === VALID_FIELDS.POSTAL_CODE ? '100' : undefined,
      paddingInlineEnd: needsIdiosyncraticStyling && type === VALID_FIELDS.ADMIN_AREA ? '100' : undefined,
      size: GRID_SIZE_FROM_FIELD[width] || 12,
      children: type === VALID_FIELDS.ADMIN_AREA ? /*#__PURE__*/_jsx(FullAddressAdminArea, {
        disabled: disabled,
        isUS: isUS,
        onChange: event => onChange(key, event),
        states: states,
        initialValue: fullAddress[key] || '',
        isShippingAddress: isShippingAddress
      }) : /*#__PURE__*/_jsx(FullAddressField, {
        disabled: disabled,
        isUS: isUS,
        onChange: event => onChange(key, event),
        type: type,
        value: fullAddress[key] || '',
        isShippingAddress: isShippingAddress
      })
    }, key))]
  });
};
export default FullAddress;